import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { axiosInstance } from "../../../utils/axios";
import PaginationV2 from "../../../components/PaginationV2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useDialogStore from "../../../stores/useDialogStore";
import { toast } from "react-toastify";
import { XMarkIcon, QrCodeIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import ColumnSearch from "../../../components/ColumnSearch";
import dayjs from "dayjs";
import ColumnSelector from "../../../components/ColumnSelector";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import excelIcon from "../../../assets/excel-icon.webp";
import ImportCountersModal from "../../../components/ImportCountersModal";
import ImportCountersPdfModal from "../../../components/ImportCountersPdfModal";
import { ArrowUpTrayIcon, DocumentTextIcon, BanknotesIcon, ClockIcon, PaperAirplaneIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import EditableCell from "../../../components/EditableCell";
import EditableDateCell from "../../../components/EditableDateCell";

export default function RentedDevices() {
  const { openDialog, closeDialog } = useDialogStore();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  const [selectedColumns, setSelectedColumns] = useState({
    unique_id: true,
    serial_number: true,
    client_id: true,
    client_name: true,
    brand: true,
    status: true,
    location: true,
    email: true,
    phone: true,
    monthly_rental_price: true,
    black_white_price: true,
    color_price: true,
    black_white_counter: true,
    color_counter: true,
    last_billed_black_white_counter: true,
    last_billed_color_counter: true,
    bw_counter_diff: true,
    color_counter_diff: true,
    contract_start_date: true,
    expected_end_date: true,
    actual_end_date: true,
    last_date_of_counter_activation: true,
    paid_through: true,
  });

  const columnOptions = [
    { key: "unique_id", label: "Unique ID" },
    { key: "serial_number", label: "Serial Number" },
    { key: "client_id", label: "Client Unique Number" },
    { key: "client_name", label: "Client Name" },
    { key: "brand", label: "Brand" },
    { key: "status", label: "Status" },
    { key: "location", label: "Location" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "monthly_rental_price", label: "Monthly Rental Price" },
    { key: "black_white_price", label: "B/W Price" },
    { key: "color_price", label: "Color Price" },
    { key: "black_white_counter", label: "B/W Counter" },
    { key: "color_counter", label: "Color Counter" },
    { key: "last_billed_black_white_counter", label: "Last Billed B/W Counter" },
    { key: "last_billed_color_counter", label: "Last Billed Color Counter" },
    { key: "bw_counter_diff", label: "B/W Counter Diff" },
    { key: "color_counter_diff", label: "Color Counter Diff" },
    { key: "contract_start_date", label: "Contract Start" },
    { key: "expected_end_date", label: "Expected End" },
    { key: "actual_end_date", label: "Actual End" },
    { key: "last_date_of_counter_activation", label: "Last Date of Counter Activation" },
    { key: "paid_through", label: "Paid Through" },
  ];

  const ColumnSelectorContent = ({ onExport }) => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);

    return (
      <div className="bg-white rounded-lg w-full p-4">
        <ColumnSelector
          columns={columnOptions}
          selectedColumns={localSelectedColumns}
          setSelectedColumns={setLocalSelectedColumns}
        />
        <div className="mt-6 flex justify-end space-x-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setSelectedColumns(localSelectedColumns);
              onExport(localSelectedColumns);
            }}
          >
            {exportMutation?.isPending ? (
              <div className="flex items-center gap-2">
                <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : (
              "Export"
            )}
          </Button>
        </div>
      </div>
    );
  };

  const handleOpenColumnSelector = () => {
    openDialog({
      title: "Select Columns to Export",
      content: (
        <ColumnSelectorContent
          onExport={(finalColumns) => {
            handleExport(finalColumns);
          }}
        />
      ),
    });
  };

  const exportMutation = useMutation({
    mutationFn: async (selectedColumnss) => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/device-rentals`,
        {
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
        },
        {
          responseType: "blob",
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `rented_devices_export_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      closeDialog();
      toast.success("Report exported successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  const handleExport = async (selectedColumnss) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate(selectedColumnss);
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  const editCellMutation = useMutation({
    mutationFn: ({ id, field, value }) =>
      axiosInstance.put(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/${id}`,
        { [field]: value }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["device-rentals"]);
      toast.success("Rental updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update rental. Please try again.");
      console.error("Update rental error:", error);
    },
  });

  const {
    data: rentalsData,
    isLoading,
  } = useQuery({
    queryKey: ["device-rentals", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals?${params.toString()}`
      );
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0,
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page");
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["device-rentals"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["device-rentals"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["device-rentals"]);
  };

  const rentals = rentalsData?.data || [];
  const paginationData = {
    current_page: rentalsData?.current_page || 1,
    last_page: rentalsData?.last_page || 1,
    next_page_url: rentalsData?.next_page_url,
    prev_page_url: rentalsData?.prev_page_url,
  };

  const [returnDialogId, setReturnDialogId] = useState(null);
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnError, setReturnError] = useState(null);

  const [datePickerId, setDatePickerId] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [datePickerLoading, setDatePickerLoading] = useState(false);
  const [datePickerError, setDatePickerError] = useState(null);

  const handleSetActualEndDate = async (id) => {
    if (!selectedDate) {
      setDatePickerError("Please select a date.");
      return;
    }
    setDatePickerLoading(true);
    setDatePickerError(null);
    try {
      await axiosInstance.patch(`${window.REACT_APP_API_ENDPOINT}/device-rentals/${id}/end-contract`, {
        actual_end_date: selectedDate,
      });
      toast.success("Actual end date updated successfully.");
      setDatePickerId(null);
      setSelectedDate("");
      queryClient.invalidateQueries(["device-rentals"]);
    } catch (error) {
      setDatePickerError("Failed to update date. Please try again.");
    } finally {
      setDatePickerLoading(false);
    }
  };

  const handleReturnDevice = async (id) => {
    setReturnLoading(true);
    setReturnError(null);
    try {
      await axiosInstance.put(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/${id}?return_device=true`,
        {
          status: "Returned",
          actual_end_date: dayjs().format("YYYY-MM-DD"),
        }
      );
      toast.success("Device returned to device list.");
      setReturnDialogId(null);
      queryClient.invalidateQueries(["device-rentals"]);
    } catch (error) {
      setReturnError("Failed to return device. Try again.");
    } finally {
      setReturnLoading(false);
    }
  };

  const deleteRentalMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`${window.REACT_APP_API_ENDPOINT}/device-rentals/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["device-rentals"]);
      toast.success("Rental deleted successfully!");
    },
    onError: (error) => {
      console.error("Delete rental error:", error);
      toast.error("Failed to delete rental. Please try again.");
    },
  });

  const handleDelete = (id) => {
    openDialog({
      title: "Delete Rental",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to delete this rental?
            <br />
            <span className="text-red-500 text-sm font-bold">
              (This action cannot be undone)
            </span>
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={() => {
                deleteRentalMutation.mutate(id);
                closeDialog();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  // ── AbaNinja: Send active rentals to AbaNinja, grouped by client ─────────

  const sendRentalToAbaNinjaMutation = useMutation({
    mutationFn: async (data) =>
      axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/abaninja/send-invoice`,
        data
      ),
    onSuccess: () => {
      toast.success("Successfully sent to AbaNinja!");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to save AbaNinja record."
      );
    },
  });

  const getInvoiceMonths = (periodStart, periodEnd) => {
    if (!periodStart || !periodEnd) return [];
    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    const months = [];
    while (cursor <= last) {
      months.push(
        cursor.toLocaleString("default", { month: "long", year: "numeric" })
      );
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return months;
  };

  const getCurrentQuarterInfo = () => {
    const month = dayjs().month();
    if (month <= 2) return { quarter: "Q1", quarterMonths: ["January", "February", "March"] };
    if (month <= 5) return { quarter: "Q2", quarterMonths: ["April", "May", "June"] };
    if (month <= 8) return { quarter: "Q3", quarterMonths: ["July", "August", "September"] };
    return { quarter: "Q4", quarterMonths: ["October", "November", "December"] };
  };

  const getPaidMonths = (lastBillingPeriodStart, nextBilling) => {
    const { quarterMonths } = getCurrentQuarterInfo();

    if (!lastBillingPeriodStart) return quarterMonths;

    const start = dayjs(lastBillingPeriodStart).startOf("month");
    const end = nextBilling ? dayjs(nextBilling).startOf("month") : dayjs().startOf("month");

    const months = [];
    let cursor = start.clone();
    while (cursor.isBefore(end) || cursor.isSame(end, "month")) {
      const name = cursor.format("MMMM");
      if (quarterMonths.includes(name)) months.push(name);
      cursor = cursor.add(1, "month");
    }

    return months.length ? months : quarterMonths;
  };

  const AbaNinjaRentalsModal = () => {
    const [isSending, setIsSending] = useState(false);
    const [sentClients, setSentClients] = useState([]);

    const { data: activeRentalsData, isLoading: activeRentalsLoading } = useQuery({
      queryKey: ["active-device-rentals-abaninja"],
      queryFn: async () => {
        const response = await axiosInstance.get(
          `${window.REACT_APP_API_ENDPOINT}/device-rentals?status=Active&per_page=1000`
        );
        return response.data;
      },
      staleTime: 0,
    });

    const activeRentals = activeRentalsData?.data ?? [];

    const groupedByClient = useMemo(() => {
      const map = {};
      activeRentals.forEach((rental) => {
        const clientId = rental.client_id;
        const clientName = rental.client_name || rental.client?.name;
        const clientUniqueId = rental.client?.unique_id;

        const key = clientId || clientName || "unknown";
        if (!map[key]) {
          map[key] = {
            client_id: clientId,
            client_name: clientName,
            client_unique_id: clientUniqueId,
            rentals: [],
          };
        }
        map[key].rentals.push(rental);
      });
      return Object.values(map);
    }, [activeRentals]);

    const handleSubmit = async () => {
      if (!groupedByClient.length) {
        toast.error("No active rentals found.");
        return;
      }

      setIsSending(true);

      const ABA_ACCOUNT_UUID = window.REACT_APP_ABANINJA_ACCOUNT_UUID;
      const ABA_API_KEY = window.REACT_APP_ABANINJA_API_KEY;
      const abaHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${ABA_API_KEY}`,
      };

      let abaPersons = [];
      let abaCompanies = [];
      try {
        const [personsRes, companiesRes] = await Promise.all([
          fetch(
            `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/addresses/v2/persons?limit=500`,
            { headers: abaHeaders }
          ),
          fetch(
            `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/addresses/v2/companies?limit=500`,
            { headers: abaHeaders }
          ),
        ]);
        if (personsRes.ok) {
          const d = await personsRes.json();
          abaPersons = d?.data ?? [];
        }
        if (companiesRes.ok) {
          const d = await companiesRes.json();
          abaCompanies = d?.data ?? [];
        }
      } catch (err) {
        toast.error("Failed to fetch AbaNinja addresses.");
        setIsSending(false);
        return;
      }

      let bankAccounts = [];
      try {
        const bankRes = await fetch(
          `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/finances/v2/bank-accounts`,
          { headers: abaHeaders }
        );
        if (bankRes.ok) {
          const d = await bankRes.json();
          bankAccounts = d?.data ?? [];
        }
      } catch (err) {
        toast.error("Failed to fetch AbaNinja bank accounts.");
        setIsSending(false);
        return;
      }

      let successCount = 0;
      const newlySent = [];

      for (const clientGroup of groupedByClient) {
        try {
          const { client_id, client_name, client_unique_id, rentals: clientRentals } = clientGroup;

          const today = dayjs();
          const firstRental = clientRentals[0];
          const nextBilling = firstRental.next_billing ? dayjs(firstRental.next_billing) : null;
          const invoiceNumberId = firstRental.invoice_number_id;

          let isUpdate = !!invoiceNumberId;
          let isEndOfCycle = nextBilling ? today.isSame(nextBilling, "month") : false;
          let monthsToCharge = 1;
          let monthsNames = [];

          if (firstRental.last_billing_period_start && firstRental.last_billed_month) {
            let cursor = dayjs(firstRental.last_billing_period_start).startOf("month");
            const last = dayjs(firstRental.last_billed_month).startOf("month");
            while (cursor.isBefore(last) || cursor.isSame(last, "month")) {
              monthsNames.push(cursor.format("MMMM"));
              cursor = cursor.add(1, "month");
            }
          }

          if (isUpdate) {
            if (firstRental.last_billing_period_start) {
              const lastStart = dayjs(firstRental.last_billing_period_start);
              monthsToCharge = today.diff(lastStart, "month");
              if (monthsToCharge <= 0) monthsToCharge = 1;
            } else if (nextBilling) {
              const diff = nextBilling.diff(today, "month");
              monthsToCharge = Math.max(1, 3 - diff);
            }
          } else if (isEndOfCycle) {
            monthsToCharge = 3;
          } else {
            monthsToCharge = 1;
          }

          let matchedAbaAddress = null;
          let isCompanyAddress = false;

          if (client_unique_id) {
            matchedAbaAddress = abaPersons.find(
              (p) => String(p.customer_number) === String(client_unique_id)
            );
            if (!matchedAbaAddress) {
              matchedAbaAddress = abaCompanies.find(
                (c) => String(c.customer_number) === String(client_unique_id)
              );
              if (matchedAbaAddress) isCompanyAddress = true;
            }
          }

          if (!matchedAbaAddress && client_name) {
            matchedAbaAddress = abaPersons.find(
              (p) => p.name?.toLowerCase() === client_name.toLowerCase()
            );
            if (!matchedAbaAddress) {
              matchedAbaAddress = abaCompanies.find(
                (c) => c.name?.toLowerCase() === client_name.toLowerCase()
              );
              if (matchedAbaAddress) isCompanyAddress = true;
            }
          }

          if (!matchedAbaAddress) {
            toast.warning(
              `Could not find AbaNinja address for client "${client_name}" (unique_id: ${client_unique_id || "none"}). Skipping.`
            );
            continue;
          }

          const currencyCode = matchedAbaAddress.currency_code || "CHF";
          const isChCustomer = currencyCode === "CHF";
          const targetBankName = isChCustomer ? "ZKB CHF" : "ZKB Euro Export";
          const bank = bankAccounts.find((b) =>
            b.name?.toLowerCase().includes(targetBankName.toLowerCase())
          );

          if (!bank?.iban) {
            toast.warning(
              `Bank "${targetBankName}" not found in AbaNinja for client "${client_name}". Skipping.`
            );
            continue;
          }

          const invoiceDateStr = today.format("YYYY-MM-DD");
          const due = today.add(isChCustomer ? 30 : 0, "day");
          const dueDateStr = due.format("YYYY-MM-DD");

          const positions = [];
          let posNum = 1;

          clientRentals.forEach((rental) => {
            const monthlyPrice = parseFloat(rental.monthly_rental_price) || 0;
            const bwPrice = parseFloat(rental.black_white_price) || 0;
            const colorPrice = parseFloat(rental.color_price) || 0;

            const bwPrev = parseFloat(rental.last_billed_black_white_counter) || 0;
            const bwCurrent = parseFloat(rental.black_white_counter) || 0;
            const colorPrev = parseFloat(rental.last_billed_color_counter) || 0;
            const colorCurrent = parseFloat(rental.color_counter) || 0;

            const allCountersAreZero =
              bwPrev === 0 && bwCurrent === 0 && colorPrev === 0 && colorCurrent === 0;

            const bwDiff = allCountersAreZero ? 0 : bwCurrent - bwPrev;
            const colorDiff = allCountersAreZero ? 0 : colorCurrent - colorPrev;

            const rentTotal = monthlyPrice * monthsToCharge;
            const bwTotal = allCountersAreZero ? 0 : bwPrice * bwDiff;
            const colorTotal = allCountersAreZero ? 0 : colorPrice * colorDiff;

            const totalForThisDevice = rentTotal + bwTotal + colorTotal;

            if (totalForThisDevice === 0) return;

            const deviceName = `${rental.brand}${rental.serial_number ? ` (S/N: ${rental.serial_number})` : ""}`;
            const descriptionParts = [];

            if (monthsNames.length > 0) {
              descriptionParts.push(`Rented for Months: ${monthsNames.join(", ")}`);
            }

            descriptionParts.push(
              `Monthly Rental (${monthsToCharge} month${monthsToCharge !== 1 ? "s" : ""}): CHF ${rentTotal.toFixed(2)}`
            );

            if (!allCountersAreZero && bwDiff !== 0) {
              descriptionParts.push(
                `B/W Copies (${bwDiff} pcs, old counter: ${bwPrev} → new counter: ${bwCurrent}): CHF ${bwTotal.toFixed(2)}`
              );
            }

            if (!allCountersAreZero && colorDiff !== 0) {
              descriptionParts.push(
                `Color Copies (${colorDiff} pcs, old counter: ${colorPrev} → new counter: ${colorCurrent}): CHF ${colorTotal.toFixed(2)}`
              );
            }

            const combinedDescription = `Device: ${deviceName}\n${descriptionParts.join("\n")}`;

            positions.push({
              kind: "product",
              positionNumber: posNum++,
              productNumber: rental.serial_number || rental.brand,
              productDescription: combinedDescription,
              quantity: 1,
              singlePrice: totalForThisDevice,
              positionTotal: totalForThisDevice,
              discount: { amount: 0 },
              vat: { percentage: 0, amount: 0 },
            });
          });

          if (!positions.length) {
            toast.warning(`No priced positions for client "${client_name}". Skipping.`);
            continue;
          }

          const documentTotal = positions.reduce((sum, p) => sum + (p.positionTotal || 0), 0);
          const referenceLabel = `RENTAL-${client_id}-${invoiceDateStr}`;

          const documentData = {
            isTemplate: false,
            receiver: {
              addressUuid: matchedAbaAddress.addresses?.[0]?.uuid ?? undefined,
              ...(isCompanyAddress
                ? { companyUuid: matchedAbaAddress.uuid }
                : { personUuid: matchedAbaAddress.uuid }),
            },
            currencyCode,
            title: `Rental Invoice — ${client_name}`,
            reference: "",
            invoiceDate: invoiceDateStr,
            dueDate: dueDateStr,
            pricesIncludeVat: false,
            documentTotal,
            paymentInstructions: {
              iban: bank.iban,
            },
            publicNotes: `Device rental invoice for ${client_name}\nPeriod: ${invoiceDateStr}`,
            footerText: "",
            positions,
          };

          let abaResult = null;
          let abaRes = null;

          if (isUpdate) {
            try {
              const localAbaRes = await axiosInstance.get(
                `${window.REACT_APP_API_ENDPOINT}/abaninja/invoices/by-invoice-number/${invoiceNumberId}`
              );
              const abaninjaUuid = localAbaRes.data.data.abaninja_uuid;

              if (!abaninjaUuid) throw new Error("AbaNinja UUID not found for update.");

              abaRes = await fetch(
                `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/documents/v2/invoices/${abaninjaUuid}`,
                {
                  method: "PATCH",
                  headers: abaHeaders,
                  body: JSON.stringify(documentData),
                }
              );
              abaResult = await abaRes.json();
            } catch (err) {
              console.error("Fetch AbaNinja UUID error", err);
              toast.error(`Failed to find AbaNinja invoice for update: ${err.message}`);
              continue;
            }
          } else {
            const { quarter, quarterMonths } = getCurrentQuarterInfo();
            const paidMonths = getPaidMonths(
              firstRental.last_billing_period_start,
              firstRental.next_billing
            );

            let paymentRecord = null;
            const totalAmount = clientRentals.reduce((sum, rental) => {
              const monthlyPrice = parseFloat(rental.monthly_rental_price) || 0;
              const bwPrice = parseFloat(rental.black_white_price) || 0;
              const colorPrice = parseFloat(rental.color_price) || 0;

              const bwPrev = parseFloat(rental.last_billed_black_white_counter) || 0;
              const bwCurrent = parseFloat(rental.black_white_counter) || 0;
              const colorPrev = parseFloat(rental.last_billed_color_counter) || 0;
              const colorCurrent = parseFloat(rental.color_counter) || 0;

              const allCountersAreZero =
                bwPrev === 0 && bwCurrent === 0 && colorPrev === 0 && colorCurrent === 0;

              const bwDiff = allCountersAreZero ? 0 : bwCurrent - bwPrev;
              const colorDiff = allCountersAreZero ? 0 : colorCurrent - colorPrev;

              const rentTotal = monthlyPrice * paidMonths.length;
              const bwTotal = allCountersAreZero ? 0 : bwPrice * bwDiff;
              const colorTotal = allCountersAreZero ? 0 : colorPrice * colorDiff;

              return sum + rentTotal + bwTotal + colorTotal;
            }, 0);
            try {
              const paymentRes = await axiosInstance.post(
                `${window.REACT_APP_API_ENDPOINT}/device-rentals-payments`,
                {
                  device_rental_id: firstRental.id,
                  quarter,
                  total_amount: totalAmount,
                  paid_months: paidMonths,
                  year: today.year(),
                  payment_date: today.format("YYYY-MM-DD"),
                }
              );
              paymentRecord = paymentRes.data?.data ?? paymentRes.data ?? null;
            } catch (payErr) {
              console.error("device_rentals_payments creation error", payErr);
              toast.error(
                `Failed to create payment record for "${client_name}": ${payErr?.response?.data?.message || payErr.message}. Skipping.`
              );
              continue;
            }

            const resolvedPaidMonths =
              (paymentRecord?.paid_months
                ? (Array.isArray(paymentRecord.paid_months)
                  ? paymentRecord.paid_months
                  : JSON.parse(paymentRecord.paid_months))
                : paidMonths);

            const buildDescription = (rental) => {
              const monthlyPrice = parseFloat(rental.monthly_rental_price) || 0;
              const bwPrice = parseFloat(rental.black_white_price) || 0;
              const colorPrice = parseFloat(rental.color_price) || 0;

              const bwPrev = parseFloat(rental.last_billed_black_white_counter) || 0;
              const bwCurrent = parseFloat(rental.black_white_counter) || 0;
              const colorPrev = parseFloat(rental.last_billed_color_counter) || 0;
              const colorCurrent = parseFloat(rental.color_counter) || 0;

              const allCountersAreZero =
                bwPrev === 0 && bwCurrent === 0 && colorPrev === 0 && colorCurrent === 0;

              const bwDiff = allCountersAreZero ? 0 : bwCurrent - bwPrev;
              const colorDiff = allCountersAreZero ? 0 : colorCurrent - colorPrev;

              const rentTotal = monthlyPrice * resolvedPaidMonths.length;
              const bwTotal = allCountersAreZero ? 0 : bwPrice * bwDiff;
              const colorTotal = allCountersAreZero ? 0 : colorPrice * colorDiff;

              const totalForThisDevice = rentTotal + bwTotal + colorTotal;
              if (totalForThisDevice === 0) return null;

              const deviceName = `${rental.brand}${rental.serial_number ? ` (S/N: ${rental.serial_number})` : ""}`;
              const descParts = [];

              descParts.push(
                `Monthly Rental (${resolvedPaidMonths.join(", ")}): CHF ${rentTotal.toFixed(2)}`
              );

              if (!allCountersAreZero && bwDiff !== 0) {
                descParts.push(
                  `B/W Copies (${bwDiff} pcs, old counter: ${bwPrev} → new counter: ${bwCurrent}): CHF ${bwTotal.toFixed(2)}`
                );
              }
              if (!allCountersAreZero && colorDiff !== 0) {
                descParts.push(
                  `Color Copies (${colorDiff} pcs, old counter: ${colorPrev} → new counter: ${colorCurrent}): CHF ${colorTotal.toFixed(2)}`
                );
              }

              return {
                description: `Device: ${deviceName}\n${descParts.join("\n")}`,
                total: totalForThisDevice,
              };
            };

            const updatedPositions = [];
            let posNumNew = 1;
            clientRentals.forEach((rental) => {
              const built = buildDescription(rental);
              if (!built) return;
              updatedPositions.push({
                kind: "product",
                positionNumber: posNumNew++,
                productNumber: rental.serial_number || rental.brand,
                productDescription: built.description,
                quantity: 1,
                singlePrice: built.total,
                positionTotal: built.total,
                discount: { amount: 0 },
                vat: { percentage: 0, amount: 0 },
              });
            });

            documentData.positions = updatedPositions.length ? updatedPositions : positions;
            documentData.documentTotal = documentData.positions.reduce(
              (sum, p) => sum + (p.positionTotal || 0), 0
            );

            abaRes = await fetch(
              `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/documents/v2/invoices`,
              {
                method: "POST",
                headers: abaHeaders,
                body: JSON.stringify({ documents: [documentData] }),
              }
            );
            abaResult = await abaRes.json();
          }

          if (!abaRes.ok) {
            console.error("AbaNinja error", abaResult);
            toast.error(
              `AbaNinja error for "${client_name}": ${abaResult?.message || abaResult?.meta?.errors?.[0]?.error || "Unknown error"}`
            );
            continue;
          }

          const newAbaDocument = isUpdate ? abaResult?.data : abaResult?.data?.documents?.[0];
          const newAbaInvoiceNumber = newAbaDocument?.invoiceNumber ?? invoiceNumberId;
          const newAbaUuid = newAbaDocument?.uuid ?? null;
          const currentDate = today.format("YYYY-MM-DD");

          if (!isUpdate) {
            await sendRentalToAbaNinjaMutation.mutateAsync({
              invoice_number_id: newAbaInvoiceNumber,
              abaninja_uuid: newAbaUuid,
              last_billed_date: currentDate,
              invoice_number_initial_value: referenceLabel,
              abaninja_invoice_id: newAbaInvoiceNumber,
              client_id: client_id,
              client_name: client_name,
              rental_ids: clientRentals.map((r) => r.id),
            });
          }

          await Promise.all(
            clientRentals.map((rental) => {
              const patchData = {};
              if (isUpdate) {
                patchData.last_billing_period_start = currentDate;
                patchData.total = documentTotal;

                if (isEndOfCycle) {
                  const nextNextBilling = nextBilling.add(3, "month").format("YYYY-MM-DD");
                  patchData.next_billing = nextNextBilling;
                  patchData.last_billing_period_end = nextNextBilling;
                  patchData.total = 0;
                  patchData.invoice_number_id = null;
                  patchData.last_billed_month = currentDate;
                  patchData.last_billing_period_start = nextBilling.subtract(2, "month").format("YYYY-MM-DD");
                }
              } else if (isEndOfCycle) {
                const nextNextBilling = nextBilling.add(3, "month").format("YYYY-MM-DD");
                patchData.next_billing = nextNextBilling;
                patchData.last_billing_period_end = nextNextBilling;
                patchData.total = 0;
                patchData.invoice_number_id = null;
                patchData.last_billed_month = currentDate;
                patchData.last_billing_period_start = nextBilling.subtract(2, "month").format("YYYY-MM-DD");
              } else {
                patchData.invoice_number_id = newAbaInvoiceNumber;
              }
              return axiosInstance.patch(
                `${window.REACT_APP_API_ENDPOINT}/device-rentals/${rental.id}`,
                patchData
              );
            })
          );

          newlySent.push(client_name);
          successCount++;
        } catch (err) {
          console.error("General error", err);
          toast.error(`Error processing client group: ${err.message}`);
        }
      }

      setSentClients(newlySent);
      setIsSending(false);

      if (successCount > 0) {
        toast.success(
          `Successfully created ${successCount} invoice(s) in AbaNinja!`
        );
        closeDialog();
      }
    };

    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          The following active rentals will be grouped by client and sent to AbaNinja as separate invoices.
          Each device's total rental amount (from the system) will become a single line item.
        </p>

        {activeRentalsLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingComponent />
            <span className="ml-2 text-gray-600">Loading active rentals…</span>
          </div>
        ) : groupedByClient.length === 0 ? (
          <NoData message="No active rentals found." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Devices</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {groupedByClient.map((clientGroup, i) => {
                  const wasSent = sentClients.includes(clientGroup.client_name);

                  return (
                    <tr key={clientGroup.client_id ?? i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                        {clientGroup.client_name || "—"}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {clientGroup.client_id || "—"}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {clientGroup.rentals.length}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {wasSent ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Active
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={isSending || activeRentalsLoading || groupedByClient.length === 0}
          >
            {isSending ? (
              <div className="flex items-center gap-2">
                <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin" />
                Sending…
              </div>
            ) : (
              `Send ${groupedByClient.length} Invoice(s) to AbaNinja`
            )}
          </Button>
        </div>
      </div>
    );
  };

  const handleOpenAbaNinjaModal = () => {
    openDialog({
      title: "Send Rental Invoices to AbaNinja",
      content: <AbaNinjaRentalsModal />,
    });
  };

  // ── Single Rental AbaNinja Modal ─────────────────────────────────────────
  const [singleAbaNinjaRental, setSingleAbaNinjaRental] = useState(null);

  const SingleRentalAbaNinjaModal = ({ rental, onClose }) => {
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState(null);

    const handleSendSingle = async () => {
      setIsSending(true);
      setError(null);

      try {
        const ABA_ACCOUNT_UUID = window.REACT_APP_ABANINJA_ACCOUNT_UUID;
        const ABA_API_KEY = window.REACT_APP_ABANINJA_API_KEY;
        const abaHeaders = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ABA_API_KEY}`,
        };

        // Fetch AbaNinja addresses & bank accounts
        const [personsRes, companiesRes, bankRes] = await Promise.all([
          fetch(`https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/addresses/v2/persons?limit=500`, { headers: abaHeaders }),
          fetch(`https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/addresses/v2/companies?limit=500`, { headers: abaHeaders }),
          fetch(`https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/finances/v2/bank-accounts`, { headers: abaHeaders }),
        ]);

        const abaPersons = personsRes.ok ? (await personsRes.json())?.data ?? [] : [];
        const abaCompanies = companiesRes.ok ? (await companiesRes.json())?.data ?? [] : [];
        const bankAccounts = bankRes.ok ? (await bankRes.json())?.data ?? [] : [];

        const clientName = rental.client_name || rental.client?.name;
        const clientUniqueId = rental.client?.unique_id;
        let matchedAbaAddress = null;
        let isCompanyAddress = false;

        if (clientUniqueId) {
          matchedAbaAddress = abaPersons.find(p => String(p.customer_number) === String(clientUniqueId));
          if (!matchedAbaAddress) {
            matchedAbaAddress = abaCompanies.find(c => String(c.customer_number) === String(clientUniqueId));
            if (matchedAbaAddress) isCompanyAddress = true;
          }
        }
        if (!matchedAbaAddress && clientName) {
          matchedAbaAddress = abaPersons.find(p => p.name?.toLowerCase() === clientName.toLowerCase());
          if (!matchedAbaAddress) {
            matchedAbaAddress = abaCompanies.find(c => c.name?.toLowerCase() === clientName.toLowerCase());
            if (matchedAbaAddress) isCompanyAddress = true;
          }
        }

        if (!matchedAbaAddress) {
          throw new Error(`No AbaNinja address found for client "${clientName}"`);
        }

        const currencyCode = matchedAbaAddress.currency_code || "CHF";
        const isChCustomer = currencyCode === "CHF";
        const targetBankName = isChCustomer ? "ZKB CHF" : "ZKB Euro Export";
        const bank = bankAccounts.find(b => b.name?.toLowerCase().includes(targetBankName.toLowerCase()));
        if (!bank?.iban) {
          throw new Error(`Bank "${targetBankName}" not found in AbaNinja`);
        }

        const today = dayjs();
        const invoiceDateStr = today.format("YYYY-MM-DD");
        const dueDateStr = today.add(isChCustomer ? 30 : 0, "day").format("YYYY-MM-DD");

        const nextBilling = rental.next_billing ? dayjs(rental.next_billing) : null;
        const invoiceNumberId = rental.invoice_number_id;
        const isUpdate = !!invoiceNumberId;
        const isEndOfCycle = nextBilling ? today.isSame(nextBilling, "month") : false;

        let monthsToCharge = 1;
        if (isUpdate) {
          if (rental.last_billing_period_start) {
            const lastStart = dayjs(rental.last_billing_period_start);
            monthsToCharge = today.diff(lastStart, "month");
            if (monthsToCharge <= 0) monthsToCharge = 1;
          } else if (nextBilling) {
            const diff = nextBilling.diff(today, "month");
            monthsToCharge = Math.max(1, 3 - diff);
          }
        } else if (isEndOfCycle) {
          monthsToCharge = 3;
        }

        const monthlyPrice = parseFloat(rental.monthly_rental_price) || 0;
        const bwPrice = parseFloat(rental.black_white_price) || 0;
        const colorPrice = parseFloat(rental.color_price) || 0;
        const bwPrev = parseFloat(rental.last_billed_black_white_counter) || 0;
        const bwCurrent = parseFloat(rental.black_white_counter) || 0;
        const colorPrev = parseFloat(rental.last_billed_color_counter) || 0;
        const colorCurrent = parseFloat(rental.color_counter) || 0;

        const allCountersAreZero = bwPrev === 0 && bwCurrent === 0 && colorPrev === 0 && colorCurrent === 0;
        const bwDiff = allCountersAreZero ? 0 : bwCurrent - bwPrev;
        const colorDiff = allCountersAreZero ? 0 : colorCurrent - colorPrev;

        const rentTotal = monthlyPrice * monthsToCharge;
        const bwTotal = allCountersAreZero ? 0 : bwPrice * bwDiff;
        const colorTotal = allCountersAreZero ? 0 : colorPrice * colorDiff;
        const totalForDevice = rentTotal + bwTotal + colorTotal;

        if (totalForDevice === 0) {
          throw new Error("No chargeable amount for this rental");
        }

        const deviceName = `${rental.brand}${rental.serial_number ? ` (S/N: ${rental.serial_number})` : ""}`;
        let positions = [];
        let documentData = {};

        if (isUpdate) {
          const descriptionParts = [];
          descriptionParts.push(`Monthly Rental (${monthsToCharge} month${monthsToCharge !== 1 ? "s" : ""}): CHF ${rentTotal.toFixed(2)}`);
          if (!allCountersAreZero && bwDiff !== 0) {
            descriptionParts.push(`B/W Copies (${bwDiff} pcs): CHF ${bwTotal.toFixed(2)}`);
          }
          if (!allCountersAreZero && colorDiff !== 0) {
            descriptionParts.push(`Color Copies (${colorDiff} pcs): CHF ${colorTotal.toFixed(2)}`);
          }

          positions = [{
            kind: "product",
            positionNumber: 1,
            productNumber: rental.serial_number || rental.brand,
            productDescription: `Device: ${deviceName}\n${descriptionParts.join("\n")}`,
            quantity: 1,
            singlePrice: totalForDevice,
            positionTotal: totalForDevice,
            discount: { amount: 0 },
            vat: { percentage: 0, amount: 0 },
          }];
        }

        documentData = {
          isTemplate: false,
          receiver: {
            addressUuid: matchedAbaAddress.addresses?.[0]?.uuid ?? undefined,
            ...(isCompanyAddress
              ? { companyUuid: matchedAbaAddress.uuid }
              : { personUuid: matchedAbaAddress.uuid }),
          },
          currencyCode,
          title: `Rental Invoice — ${clientName}`,
          reference: "",
          invoiceDate: invoiceDateStr,
          dueDate: dueDateStr,
          pricesIncludeVat: false,
          documentTotal: totalForDevice,
          paymentInstructions: { iban: bank.iban },
          publicNotes: `Device rental invoice for ${clientName}\nPeriod: ${invoiceDateStr}`,
          footerText: "",
          positions,
        };

        let abaResult, abaRes;
        let paymentRecord = null;

        if (isUpdate) {
          const localAbaRes = await axiosInstance.get(
            `${window.REACT_APP_API_ENDPOINT}/abaninja/invoices/by-invoice-number/${invoiceNumberId}`
          );
          const abaninjaUuid = localAbaRes.data.data.abaninja_uuid;
          if (!abaninjaUuid) throw new Error("AbaNinja UUID not found for update.");

          abaRes = await fetch(
            `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/documents/v2/invoices/${abaninjaUuid}`,
            {
              method: "PATCH",
              headers: abaHeaders,
              body: JSON.stringify(documentData),
            }
          );
          abaResult = await abaRes.json();
        } else {
          const { quarter } = getCurrentQuarterInfo();
          const paidMonths = getPaidMonths(
            rental.last_billing_period_start,
            rental.next_billing
          );

          const paymentRes = await axiosInstance.post(
            `${window.REACT_APP_API_ENDPOINT}/device-rentals-payments`,
            {
              device_rental_id: rental.id,
              quarter,
              total_amount: totalForDevice,
              paid_months: paidMonths,
              year: today.year(),
              payment_date: today.format("YYYY-MM-DD"),
            }
          );
          paymentRecord = paymentRes.data?.data ?? paymentRes.data;

          const resolvedPaidMonths = Array.isArray(paymentRecord.paid_months)
            ? paymentRecord.paid_months
            : JSON.parse(paymentRecord.paid_months);

          const descParts = [];
          descParts.push(`Monthly Rental (${resolvedPaidMonths.join(", ")}): CHF ${(monthlyPrice * resolvedPaidMonths.length).toFixed(2)}`);
          if (!allCountersAreZero && bwDiff !== 0) {
            descParts.push(`B/W Copies (${bwDiff} pcs): CHF ${bwTotal.toFixed(2)}`);
          }
          if (!allCountersAreZero && colorDiff !== 0) {
            descParts.push(`Color Copies (${colorDiff} pcs): CHF ${colorTotal.toFixed(2)}`);
          }

          positions = [{
            kind: "product",
            positionNumber: 1,
            productNumber: rental.serial_number || rental.brand,
            productDescription: `Device: ${deviceName}\n${descParts.join("\n")}`,
            quantity: 1,
            singlePrice: totalForDevice,
            positionTotal: totalForDevice,
            discount: { amount: 0 },
            vat: { percentage: 0, amount: 0 },
          }];

          documentData.positions = positions;
          documentData.documentTotal = totalForDevice;

          abaRes = await fetch(
            `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/documents/v2/invoices`,
            {
              method: "POST",
              headers: abaHeaders,
              body: JSON.stringify({ documents: [documentData] }),
            }
          );
          abaResult = await abaRes.json();
        }

        if (!abaRes.ok) {
          throw new Error(abaResult?.message || abaResult?.meta?.errors?.[0]?.error || "AbaNinja error");
        }

        const newAbaDocument = isUpdate ? abaResult?.data : abaResult?.data?.documents?.[0];
        const newAbaInvoiceNumber = newAbaDocument?.invoiceNumber ?? invoiceNumberId;
        const newAbaUuid = newAbaDocument?.uuid ?? null;
        const currentDate = today.format("YYYY-MM-DD");

        if (!isUpdate) {
          await sendRentalToAbaNinjaMutation.mutateAsync({
            invoice_number_id: newAbaInvoiceNumber,
            abaninja_uuid: newAbaUuid,
            last_billed_date: currentDate,
            invoice_number_initial_value: `RENTAL-${rental.client_id}-${invoiceDateStr}`,
            abaninja_invoice_id: newAbaInvoiceNumber,
            client_id: rental.client_id,
            client_name: clientName,
            rental_ids: [rental.id],
          });
        }

        const patchData = {};
        if (isUpdate) {
          patchData.last_billing_period_start = currentDate;
          patchData.total = totalForDevice;
          if (isEndOfCycle) {
            const nextNextBilling = nextBilling.add(3, "month").format("YYYY-MM-DD");
            patchData.next_billing = nextNextBilling;
            patchData.last_billing_period_end = nextNextBilling;
            patchData.total = 0;
            patchData.invoice_number_id = null;
            patchData.last_billed_month = currentDate;
            patchData.last_billing_period_start = nextBilling.subtract(2, "month").format("YYYY-MM-DD");
          }
        } else if (isEndOfCycle) {
          const nextNextBilling = nextBilling.add(3, "month").format("YYYY-MM-DD");
          patchData.next_billing = nextNextBilling;
          patchData.last_billing_period_end = nextNextBilling;
          patchData.total = 0;
          patchData.invoice_number_id = null;
          patchData.last_billed_month = currentDate;
          patchData.last_billing_period_start = nextBilling.subtract(2, "month").format("YYYY-MM-DD");
        } else {
          patchData.invoice_number_id = newAbaInvoiceNumber;
        }

        await axiosInstance.patch(
          `${window.REACT_APP_API_ENDPOINT}/device-rentals/${rental.id}`,
          patchData
        );

        toast.success("Invoice sent to AbaNinja successfully!");
        queryClient.invalidateQueries(["device-rentals"]);
        onClose();
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to send invoice");
        toast.error(err.message || "Failed to send invoice");
      } finally {
        setIsSending(false);
      }
    };

    return (
      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-900 mb-2">Rental Details</h3>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-gray-600">Client:</dt>
            <dd className="text-gray-900">{rental.client_name || rental.client?.name}</dd>
            <dt className="text-gray-600">Device:</dt>
            <dd className="text-gray-900">{rental.brand} {rental.serial_number}</dd>
            <dt className="text-gray-600">Monthly Price:</dt>
            <dd className="text-gray-900">CHF {rental.monthly_rental_price}</dd>
            <dt className="text-gray-600">B/W Counter:</dt>
            <dd className="text-gray-900">{rental.black_white_counter}</dd>
            <dt className="text-gray-600">Color Counter:</dt>
            <dd className="text-gray-900">{rental.color_counter}</dd>
          </dl>
        </div>
        {error && <div className="text-red-600 text-sm">{error}</div>}
        <div className="flex justify-end gap-2">
          <Button variant="light" size="sm" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSendSingle} disabled={isSending}>
            {isSending ? (
              <div className="flex items-center gap-2">
                <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin" />
                Sending…
              </div>
            ) : (
              "Send to AbaNinja"
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="px-6 py-4">
      <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-gray-50 rounded-lg mb-4">
        <div className="flex items-center lg:flex-row flex-col gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<XMarkIcon className="h-4 w-4" />}
            onClick={handleClearFilters}
            className="mb-2 lg:mb-0"
          >
            Clear Filters
          </Button>
          <Button
            onClick={handleOpenColumnSelector}
            className="mb-2 lg:mb-0 bg-green-600 hover:bg-green-700 text-white inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors"
          >
            <span>Export Rented Devices</span>
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            icon={<ArrowUpTrayIcon className="h-4 w-4" />}
            onClick={() =>
              openDialog({
                title: "Import Counters",
                content: (
                  <ImportCountersModal
                    onSuccess={(data) => {
                      const inactive = data?.inactive_rentals?.data ?? [];
                      inactive.forEach((rental) => {
                        toast.warning(
                          `S/N ${rental.serial_number}: Rental for "${rental.client_name}" has had the same counters (B/W: ${rental.black_white_counter}, Color: ${rental.color_counter}) for the past 3 months.`,
                          { autoClose: false }
                        );
                      });
                    }}
                  />
                ),
              })
            }
            className="mb-2 lg:mb-0"
          >
            Import Counters
          </Button>
          <Button
            variant="outline-warning"
            size="sm"
            icon={<DocumentTextIcon className="h-4 w-4" />}
            onClick={() =>
              openDialog({
                title: "Import Counters from PDF",
                content: (
                  <ImportCountersPdfModal
                    onSuccess={(data) => {
                      const inactive = data?.inactive_rentals?.data ?? [];
                      inactive.forEach((rental) => {
                        toast.warning(
                          `S/N ${rental.serial_number}: Rental for "${rental.client_name}" has had the same counters (B/W: ${rental.black_white_counter}, Color: ${rental.color_counter}) for the past 3 months.`,
                          { autoClose: false }
                        );
                      });
                    }}
                  />
                ),
              })
            }
            className="mb-2 lg:mb-0"
          >
            Add Contracts
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<DocumentTextIcon className="h-4 w-4" />}
            onClick={handleOpenAbaNinjaModal}
            className="mb-2 lg:mb-0"
          >
            Send To AbaNinja
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-black">
            <tr>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Client Unique Number"
                  columnKey="client_id"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Client Unique Number..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Client Name"
                  columnKey="client_name"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Client Name..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Brand"
                  columnKey="brand"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Brand..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Status"
                  columnKey="status"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Status..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Location"
                  columnKey="location"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Location..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Email"
                  columnKey="email"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Email..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Phone"
                  columnKey="phone"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Phone..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Unique ID"
                  columnKey="unique_id"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Unique ID..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Serial Number"
                  columnKey="serial_number"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Serial Number..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Monthly Price"
                  columnKey="monthly_rental_price"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search by Price..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="B/W Price"
                  columnKey="black_white_price"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Color Price"
                  columnKey="color_price"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="B/W Counter"
                  columnKey="black_white_counter"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Color Counter"
                  columnKey="color_counter"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  placeholder="Search..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Contract Start"
                  columnKey="contract_start_date"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  isDate={true}
                  placeholder="Search by Date..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Expected End"
                  columnKey="expected_end_date"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  isDate={true}
                  placeholder="Search by Date..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Actual End"
                  columnKey="actual_end_date"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  isDate={true}
                  placeholder="Search by Date..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm">
                <ColumnSearch
                  columnName="Last Counter Date"
                  columnKey="last_date_of_counter_activation"
                  onSearch={handleColumnSearch}
                  onSort={handleColumnSort}
                  sortConfig={sortConfig}
                  isDate={true}
                  placeholder="Search by Date..."
                />
              </th>
              <th className="px-4 py-4 text-left text-white text-sm min-w-[280px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={19} className="text-center">
                  <LoadingComponent />
                </td>
              </tr>
            ) : rentals.length === 0 ? (
              <tr>
                <td colSpan={19} className="text-center">
                  <NoData message="No rented devices found" />
                </td>
              </tr>
            ) : (
              rentals.map((rental, index) => (
                <tr
                  key={rental.id}
                  className={`divide-y divide-gray-200 ${index % 2 === 0 ? "bg-white" : "bg-gray-100"
                    }`}
                >
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {rental.client?.unique_id || rental.client_id}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.client_name || ""}
                      displayValue={rental.client_name || "---"}
                      id={rental.id}
                      field="client_name"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "client_name",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.brand || ""}
                      displayValue={rental.brand || "---"}
                      id={rental.id}
                      field="brand"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "brand",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.status || ""}
                      displayValue={
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          {rental.status || "---"}
                        </span>
                      }
                      id={rental.id}
                      field="status"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "status",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.location || ""}
                      displayValue={rental.location || "---"}
                      id={rental.id}
                      field="location"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "location",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.email || ""}
                      displayValue={rental.email || "---"}
                      id={rental.id}
                      field="email"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "email",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.phone || ""}
                      displayValue={rental.phone || "---"}
                      id={rental.id}
                      field="phone"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "phone",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {rental.device?.unique_id || "---"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.serial_number || ""}
                      displayValue={rental.serial_number || "---"}
                      id={rental.id}
                      field="serial_number"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "serial_number",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.monthly_rental_price || ""}
                      displayValue={rental.monthly_rental_price || "---"}
                      id={rental.id}
                      field="monthly_rental_price"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "monthly_rental_price",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.black_white_price || ""}
                      displayValue={rental.black_white_price || "---"}
                      id={rental.id}
                      field="black_white_price"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "black_white_price",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.color_price || ""}
                      displayValue={rental.color_price || "---"}
                      id={rental.id}
                      field="color_price"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "color_price",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.black_white_counter || ""}
                      displayValue={rental.black_white_counter || "---"}
                      id={rental.id}
                      field="black_white_counter"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "black_white_counter",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableCell
                      value={rental.color_counter || ""}
                      displayValue={rental.color_counter || "---"}
                      id={rental.id}
                      field="color_counter"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "color_counter",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableDateCell
                      value={rental.contract_start_date || ""}
                      displayValue={
                        rental.contract_start_date
                          ? dayjs(rental.contract_start_date).format("DD/MM/YYYY")
                          : "---"
                      }
                      id={rental.id}
                      field="contract_start_date"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "contract_start_date",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableDateCell
                      value={rental.expected_end_date || ""}
                      displayValue={
                        rental.expected_end_date
                          ? dayjs(rental.expected_end_date).format("DD/MM/YYYY")
                          : "---"
                      }
                      id={rental.id}
                      field="expected_end_date"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "expected_end_date",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableDateCell
                      value={rental.actual_end_date || ""}
                      displayValue={
                        rental.actual_end_date
                          ? dayjs(rental.actual_end_date).format("DD/MM/YYYY")
                          : "---"
                      }
                      id={rental.id}
                      field="actual_end_date"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "actual_end_date",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <EditableDateCell
                      value={rental.last_date_of_counter_activation || ""}
                      displayValue={
                        rental.last_date_of_counter_activation
                          ? dayjs(rental.last_date_of_counter_activation).format("DD/MM/YYYY")
                          : "---"
                      }
                      id={rental.id}
                      field="last_date_of_counter_activation"
                      onSave={(data) =>
                        editCellMutation.mutate({
                          id: data.id,
                          field: "last_date_of_counter_activation",
                          value: data.value,
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 text-gray-900 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => navigate(`/admin/rentals/${rental.id}`)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-transparent border border-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="View Details"
                    >
                      <QrCodeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/admin/rentals/${rental.id}/edit`)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-500 bg-transparent border border-amber-500 hover:bg-amber-50 rounded transition-colors"
                      title="Edit Rental"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(rental.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-transparent border border-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete Rental"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/admin/rentals/${rental.id}/payments`)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-600 bg-transparent border border-teal-600 hover:bg-teal-50 rounded transition-colors"
                      title="Rental Payment History"
                    >
                      <BanknotesIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/admin/rentals/${rental.id}/counters`)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-transparent border border-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                      title="View Counters History"
                    >
                      <ClockIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setSingleAbaNinjaRental(rental)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-600 bg-transparent border border-green-600 hover:bg-green-50 rounded transition-colors"
                      title="Send to AbaNinja"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                    </button>
                    {rental.status === "Active" && (
                      <Button
                        variant="outline"
                        size="sm"
                        title="Return Device"
                        // className="min-w-[180px]"
                        onClick={() => setReturnDialogId(rental.id)}
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                      </Button>
                    )}
                    <div className="relative">
                      <button
                        onClick={() => {
                          setDatePickerId(datePickerId === rental.id ? null : rental.id);
                          setSelectedDate(rental.actual_end_date ? dayjs(rental.actual_end_date).format("YYYY-MM-DD") : "");
                          setDatePickerError(null);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-600 bg-transparent border border-purple-600 hover:bg-purple-50 rounded transition-colors"
                        title="Set Actual End Date"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </button>
                      {datePickerId === rental.id && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
                            <div className="mb-4 text-lg font-semibold">Set Actual End Date</div>
                            <input
                              type="date"
                              value={selectedDate}
                              onChange={(e) => setSelectedDate(e.target.value)}
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                            {datePickerError && (
                              <div className="text-red-600 text-sm mt-2">{datePickerError}</div>
                            )}
                            <div className="flex justify-end gap-2 mt-4">
                              <Button
                                variant="light"
                                size="sm"
                                onClick={() => { setDatePickerId(null); setSelectedDate(""); setDatePickerError(null); }}
                                disabled={datePickerLoading}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleSetActualEndDate(rental.id)}
                                disabled={datePickerLoading}
                              >
                                {datePickerLoading ? "Saving..." : "Save"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {returnDialogId === rental.id && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
                          <div className="mb-4 text-lg font-semibold">Are you sure you want to return this rented device back to the device list?</div>
                          {returnError && <div className="text-red-600 text-sm mb-2">{returnError}</div>}
                          <div className="flex justify-end gap-2 mt-4">
                            <Button
                              variant="light"
                              size="sm"
                              onClick={() => setReturnDialogId(null)}
                              disabled={returnLoading}
                            >
                              Close
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleReturnDevice(rental.id)}
                              disabled={returnLoading}
                            >
                              {returnLoading ? "Sending..." : "Send"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <PaginationV2
        current_page={paginationData.current_page}
        last_page={paginationData.last_page}
        next_page_url={paginationData.next_page_url}
        prev_page_url={paginationData.prev_page_url}
      />

      {/* Single Rental AbaNinja Modal */}
      {singleAbaNinjaRental && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Send Rental to AbaNinja</h2>
              <button onClick={() => setSingleAbaNinjaRental(null)} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <SingleRentalAbaNinjaModal
              rental={singleAbaNinjaRental}
              onClose={() => setSingleAbaNinjaRental(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}